import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  PhoneCall, MessageSquare, UserPlus, CalendarCheck, CalendarClock, MessagesSquare,
  Activity, LifeBuoy, Rocket, ArrowRight,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, MetricCard, StatusBadge, Button, LoadingBlock, ErrorState } from "@/components/ds";
import { api, formatApiError, type DashboardData } from "@/lib/api";
import { humanizeActivity, timeAgo } from "./dashboard/constants";
import { QuickActions } from "./dashboard/QuickActions";
import { ReceptionistStatus } from "./dashboard/ReceptionistStatus";
import { IntegrationHealth } from "./dashboard/IntegrationHealth";
import { SectionCard } from "./dashboard/SectionCard";
import { Recommendations } from "./dashboard/Recommendations";

function fmtDateTime(iso?: string): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function Dashboard() {
  const { organization } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setError(null);
    api.getDashboard().then(setData).catch((e) => setError(formatApiError(e)));
  };
  useEffect(load, []);

  if (error) return <div data-testid="page-dashboard"><ErrorState message={error} onRetry={load} /></div>;
  if (!data) return <div data-testid="page-dashboard"><LoadingBlock rows={6} /></div>;

  const m = data.metrics;
  const setupIncomplete = data.onboarding_status !== "completed";

  return (
    <div data-testid="page-dashboard" className="space-y-6">
      <PageHeader
        title="Dashboard"
        description="Business outcomes from your AI front desk — updates automatically as calls and bookings come in."
        testid="dashboard-header"
      />

      {setupIncomplete && (
        <div className="flex flex-col gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="setup-banner">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white"><Rocket className="h-5 w-5" /></span>
            <div>
              <p className="text-sm font-semibold text-blue-900">Finish setting up your AI front desk</p>
              <p className="text-sm text-blue-700">Complete the guided setup to start taking live calls.</p>
            </div>
          </div>
          <Link to="/onboarding"><Button size="sm" data-testid="setup-banner-cta">Continue setup <ArrowRight className="h-4 w-4" /></Button></Link>
        </div>
      )}

      {/* Today's metrics */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="dashboard-metrics">
        <MetricCard label="Calls answered" value={m.calls_answered_today} icon={PhoneCall} hint="Today" testid="metric-calls" />
        <MetricCard label="SMS conversations" value={m.sms_conversations_today} icon={MessageSquare} hint="Today" testid="metric-sms" />
        <MetricCard label="New leads" value={m.new_leads_today} icon={UserPlus} hint="Today" testid="metric-leads" />
        <MetricCard label="Appointments booked" value={m.appointments_booked_today} icon={CalendarCheck} hint="Today" testid="metric-appointments" />
      </div>

      {/* ResFil Recommendations (Optimization Center preview) */}
      <Recommendations />

      {/* Quick actions */}
      <div>
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">Quick actions</p>
        <QuickActions />
      </div>

      {/* Receptionist + integrations */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1"><ReceptionistStatus data={data.receptionist} /></div>
        <div className="lg:col-span-2"><IntegrationHealth items={data.integrations} /></div>
      </div>

      {/* Upcoming appointments + recent conversations */}
      <div className="grid gap-6 lg:grid-cols-2">
        <SectionCard
          title="Upcoming appointments" icon={CalendarClock} testid="section-upcoming"
          isEmpty={data.upcoming_appointments.length === 0}
          emptyIcon={CalendarClock} emptyTitle="No upcoming appointments"
          emptyDescription="Booked jobs will appear here once your receptionist starts scheduling."
        >
          <ul className="divide-y divide-slate-100">
            {data.upcoming_appointments.map((a) => (
              <li key={a.id as string} className="flex items-center justify-between px-5 py-3.5" data-testid={`upcoming-${a.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900">{(a.service_name as string) ?? "Appointment"}</p>
                  <p className="truncate text-xs text-slate-500">{(a.contact_name as string) ?? "—"}</p>
                </div>
                <span className="shrink-0 text-xs text-slate-500">{fmtDateTime(a.scheduled_at as string)}</span>
              </li>
            ))}
          </ul>
        </SectionCard>

        <SectionCard
          title="Recent conversations" icon={MessagesSquare} testid="section-conversations"
          isEmpty={data.recent_conversations.length === 0}
          emptyIcon={MessagesSquare} emptyTitle="No conversations yet"
          emptyDescription="Calls and texts handled by your AI will show up here."
        >
          <ul className="divide-y divide-slate-100">
            {data.recent_conversations.map((c) => (
              <li key={c.id as string} className="flex items-center justify-between px-5 py-3.5" data-testid={`conversation-${c.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900">{(c.contact_name as string) ?? "Unknown caller"}</p>
                  <p className="truncate text-xs text-slate-500">{(c.preview as string) ?? (c.channel as string)}</p>
                </div>
                <StatusBadge tone={c.channel === "voice" ? "blue" : "slate"}>{c.channel as string}</StatusBadge>
              </li>
            ))}
          </ul>
        </SectionCard>
      </div>

      {/* Recent activity + human assistance */}
      <div className="grid gap-6 lg:grid-cols-2">
        <SectionCard
          title="Recent activity" icon={Activity} testid="section-activity"
          isEmpty={data.recent_activity.length === 0}
          emptyIcon={Activity} emptyTitle="No activity yet"
          emptyDescription="Setup changes and events will be logged here."
        >
          <ul className="divide-y divide-slate-100">
            {data.recent_activity.map((ev) => (
              <li key={ev.id as string} className="flex items-center justify-between px-5 py-3" data-testid={`activity-${ev.id}`}>
                <span className="truncate text-sm text-slate-700">{humanizeActivity(ev.action as string)}</span>
                <span className="shrink-0 text-xs text-slate-400">{timeAgo(ev.created_at as string)}</span>
              </li>
            ))}
          </ul>
        </SectionCard>

        <SectionCard
          title="Human assistance requests" icon={LifeBuoy} testid="section-assistance"
          isEmpty={data.human_assistance_requests.length === 0}
          emptyIcon={LifeBuoy} emptyTitle="No requests"
          emptyDescription="When your AI needs a human, those requests appear here."
        >
          <ul className="divide-y divide-slate-100">
            {data.human_assistance_requests.map((r) => (
              <li key={r.id as string} className="flex items-center justify-between px-5 py-3.5" data-testid={`assistance-${r.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900">{(r.contact_name as string) ?? "Caller"}</p>
                  <p className="truncate text-xs text-slate-500">{(r.reason as string) ?? "Needs a person"}</p>
                </div>
                <StatusBadge tone="amber">Open</StatusBadge>
              </li>
            ))}
          </ul>
        </SectionCard>
      </div>

      <p className="text-center text-xs text-slate-400" data-testid="dashboard-org-note">
        {organization?.name ? `${organization.name} · ` : ""}All figures are for your organization only.
      </p>
    </div>
  );
}
